// frontend/src/pages/Homepage.js
//
// The homepage - the "live mesh" design (concept 01B), cut over at P.11 of the
// homepage port. The design source is the owner-approved prototype
// docs/homepage-concepts/concept-01b-live-mesh-light.html (gitignored); every
// section under components/homepage/*Live.js is a literal port of one of its
// <section> blocks, and the tokens + keyframes live in src/homepage/homepage.css.
//
// ONE SWITCH STOPS EVERY MOVING PART: `html.lf-motion-off` (src/motion/
// motionSignal.js - set by the a11y widget's "stop animations" and by
// prefers-reduced-motion) and `window.__PRERENDER__` (scripts/prerender.js)
// both make every section render its final, static state - no rAF loops, no
// timers, no CSS animation. The prerendered snapshot is what crawlers read, so
// nothing readable may depend on an observer firing.
//
// It reuses the app's chrome verbatim - AuthLayout owns the single `dir`
// attribute and injects `isHebrew` through cloneElement, Navbar and Footer are
// the production components. Nothing here is analytics-aware on purpose:
// page_view fires from AnalyticsContext, utm_visit from CaptureAcquisition, the
// consent banner and the PWA snackbar are mounted in App.js - all app-level, all
// untouched by a homepage rewrite. The one acquisition READ here is the
// ad-visitor hero's copy switch (utils/adVisitor.js) - words only, nothing
// emitted.

import React, { Suspense, useLayoutEffect, useState } from "react";
import { Box } from "@mui/material";
import AuthLayout from "../components/AuthLayout";
import Seo, { SITE_URL } from "../components/Seo";
import HeroLive from "../components/homepage/HeroLive";
import TickerLive from "../components/homepage/TickerLive";
import ClientsLive from "../components/homepage/ClientsLive";
import FeaturesLive from "../components/homepage/FeaturesLive";
import TypesLive from "../components/homepage/TypesLive";
import NumbersLive from "../components/homepage/NumbersLive";
import CtaLive, { CtaBandLive } from "../components/homepage/CtaLive";
import PricingSection from "../components/homepage/PricingSection";
import FaqLive from "../components/homepage/FaqLive";
import BlogLive from "../components/homepage/BlogLive";
import HowItWorksLive, { SMOKE_SLOT_ID } from "../components/homepage/HowItWorksLive";
import SceneWashes from "../components/homepage/SceneWashes";
import { buildFaqJsonLd } from "../components/homepage/faqData";
import { PLAN_PRICE_DISPLAY } from "../contexts/PricingContext";
import useLiveMeshPageChrome from "../homepage/useLiveMeshPageChrome";
import useHydrated from "../motion/useHydrated";
import { isPrerendering } from "../motion/useInViewGate";
import { getAdSourceCode } from "../utils/adVisitor";
import { AD_HERO_COPY, AD_HERO_SOURCE_CODE } from "../components/homepage/adHeroCopy";
import { releaseAdHeroHold } from "../homepage/adHeroHold";
import { OFFICIAL_PROFILE_URLS } from "../content/officialProfiles";
import "../homepage/homepage.css";

// The mesh engine is the single biggest thing on this page and NOTHING readable
// depends on it, so it must not sit in the chunk that has to arrive before the
// hero can paint. Split out, mounted inside a null Suspense: if it never
// arrives, the page is a page. The split holds only while no eager module has a
// static import from MeshCanvas.js or meshEngine.js - the primitives the hero
// shares with the engine live in homepage/meshConstants.js for that reason, and
// __tests__/meshSplitBoundary.test.js fails the build-time graph if it drifts.
const MeshCanvas = React.lazy(() => import("../components/homepage/MeshCanvas"));

// SEO/GEO metadata for the homepage. Hebrew is the primary market (Israeli
// professionals), English is the paired fallback. Keywords are woven in
// naturally: לידים / leads, לקוחות פוטנציאליים / potential customers,
// הזדמנויות עסקיות / business opportunities, שיווק לעסקים / marketing.
export const HOMEPAGE_META = {
  // Descriptions are HARD-CAPPED at 160 chars (Bing site-scan flags longer;
  // Google truncates). HE is owner-authored at exactly 160 (2026-08-12, Bing
  // hygiene round). Deliberately DIFFERENT from index.html's site default -
  // identical descriptions across pages trip the duplicate-description check.
  he: {
    title: "Leadify AI - לידים והזדמנויות עסקיות בזמן אמת",
    description:
      "לידיפיי (Leadify AI) - המקום בו ההזדמנויות מוצאות אותך: סוכני AI סורקים אלפי קבוצות פייסבוק בזמן אמת, מזהים לידים ולקוחות פוטנציאליים ומתריעים מיד למייל ולנייד.",
  },
  en: {
    title: "Leadify AI - AI Agents for Leads & Business Opportunities",
    description:
      "Where opportunities find you: AI agents scan thousands of Facebook groups in real time, spot leads and potential customers, and alert you instantly on your phone and by email.",
  },
};

// The Organization `sameAs` comes from OFFICIAL_PROFILE_URLS - the single
// source of our owner-confirmed profiles (content/officialProfiles.js, which
// also feeds the footer's follow-us icons). Why it matters and the
// owner-confirmed-only rule are documented there.

// Schema.org structured data — helps Google show rich results and gives AI
// crawlers (ChatGPT / Claude / Perplexity) clean, machine-readable facts to
// quote when recommending the product.
export const buildHomepageJsonLd = (isHebrew) => {
  // The domain is stated ONCE, in prose, on purpose: answer engines were fusing
  // us with the unrelated "Leadify" at leadify.dev, and a name alone does not
  // disambiguate two products that share it. Binding the brand to its own
  // address inside the sentence is the signal that separates them.
  // NOT the same string as HOMEPAGE_META.description (that one is hard-capped
  // at 160 chars for the meta tag); this one is only read by crawlers as JSON.
  const description = isHebrew
    ? "לידיפיי (Leadify AI), בכתובת leadifyai.biz, היא פלטפורמה ישראלית ליצירת לידים שמזהה לקוחות פוטנציאליים והזדמנויות עסקיות עבור בעלי מקצוע, על בסיס ניתוח פוסטים בקבוצות פייסבוק בעזרת בינה מלאכותית."
    : "Leadify AI (leadifyai.biz) is an Israeli lead-generation platform that surfaces potential customers and business opportunities for professionals by analyzing Facebook group posts with AI.";
  // Every spelling the brand appears under in the wild - Hebrew transliteration,
  // spaced and unspaced Latin. `name` stays canonical; these are the aliases an
  // engine has to be able to resolve ONTO this entity rather than onto the
  // unrelated products sharing the "Leadify" string.
  const alternateName = ["לידיפיי", "Leadify AI", "LeadifyAI"];
  return [
    {
      "@context": "https://schema.org",
      "@type": "Organization",
      name: "Leadify AI",
      alternateName,
      url: SITE_URL,
      logo: `${SITE_URL}/android-chrome-512x512.png`,
      description,
      // Year granularity on purpose: the entity demonstrably dates to 2025 (the
      // product's first public terms), and a made-up exact date is a fact an
      // answer engine would repeat.
      foundingDate: "2025",
      // The support address published on the public /terms page. NOT /contact -
      // that route sits behind ProtectedRoute, so a crawler following it lands
      // on the login wall.
      contactPoint: {
        "@type": "ContactPoint",
        contactType: "customer support",
        email: "support@leadifyai.biz",
        availableLanguage: ["he", "en"],
      },
      // sameAs is emitted only once at least one profile is confirmed ours
      // (content/officialProfiles.js) - a wrong link hurts disambiguation.
      ...(OFFICIAL_PROFILE_URLS.length ? { sameAs: OFFICIAL_PROFILE_URLS } : {}),
    },
    {
      "@context": "https://schema.org",
      "@type": "WebSite",
      name: "Leadify AI",
      url: SITE_URL,
      inLanguage: isHebrew ? "he" : "en",
    },
    {
      "@context": "https://schema.org",
      "@type": "SoftwareApplication",
      name: "Leadify AI",
      alternateName,
      applicationCategory: "BusinessApplication",
      operatingSystem: "Web",
      url: SITE_URL,
      description,
      // The price is the one fact about us an answer engine most often cannot
      // find: until now no public page stated it in machine-readable form.
      //
      // SHAPE: repeated Offer nodes, not hasOfferCatalog. Google's
      // SoftwareApplication reference documents `offers.price` (and
      // `offers.priceCurrency` for anything above 0) and never mentions
      // hasOfferCatalog:
      // developers.google.com/search/docs/appearance/structured-data/software-app
      //
      // ONLY THE TWO PAID PLANS. The free trial is deliberately NOT a third,
      // zero-priced Offer (owner call 2026-09-06): a `price: "0"` node is the
      // one an answer engine is most likely to quote as "the price", and
      // "Leadify AI costs 0" is not the fact we want repeated. The trial is
      // stated in prose and in the FAQ, where its 14-day bound travels with it.
      //
      // Deliberately NO aggregateRating / review - Google lists a rating as
      // required for the rich result, and we have no real ratings to state.
      // Fabricating one to win a rich result is not on the table; the value
      // here is the machine-readable price, which AI engines read regardless.
      //
      // Prices are DERIVED from the offline pricing mirror, never typed here.
      offers: [
        {
          "@type": "Offer",
          name: "Solo",
          price: PLAN_PRICE_DISPLAY.solo,
          priceCurrency: "ILS",
          url: `${SITE_URL}/register`,
          availability: "https://schema.org/InStock",
        },
        {
          "@type": "Offer",
          name: "Professional",
          price: PLAN_PRICE_DISPLAY.professional,
          priceCurrency: "ILS",
          url: `${SITE_URL}/register`,
          availability: "https://schema.org/InStock",
        },
      ],
      featureList: isHebrew
        ? [
            "זיהוי לידים והזדמנויות עסקיות מקבוצות פייסבוק בעזרת בינה מלאכותית",
            "התראות בזמן אמת במייל וב-Push על כל הזדמנות רלוונטית",
            "ניסוח הודעות פנייה אישיות באמצעות AI",
            "בונה אתרים חכם ליצירת אתר עסקי מקצועי בכתובת אישית - כולל גלריה וטופס יצירת קשר",
            "כרטיס ביקור דיגיטלי תואם עם קוד QR",
            "מודיעין עסקי וניטור אזכורי מותג ומתחרים",
          ]
        : [
            "AI-powered detection of leads and business opportunities from Facebook groups",
            "Real-time email and push alerts for every relevant opportunity",
            "AI-drafted personalized outreach messages",
            "Smart website builder to create a professional business website at a personal URL - with gallery and contact form",
            "Matching digital business card with QR code",
            "Business intelligence and brand/competitor mention monitoring",
          ],
    },
    // Service — describes WHAT Leadify does in the exact terms people search for.
    // serviceType carries the head query family verbatim ("סריקת קבוצות פייסבוק",
    // "ניטור קבוצות פייסבוק", "איתור לידים"), and the description frames it as a
    // managed AI-agent service (not a DIY tool, never "automation").
    {
      "@context": "https://schema.org",
      "@type": "Service",
      name: isHebrew
        ? "סריקת קבוצות פייסבוק ואיתור הזדמנויות עסקיות בעזרת סוכני AI"
        : "Facebook group scanning and business-opportunity detection with AI agents",
      serviceType: isHebrew
        ? "סריקת קבוצות פייסבוק, ניטור קבוצות פייסבוק, איתור לידים והזדמנויות עסקיות"
        : "Facebook group scanning, Facebook group monitoring, lead and business-opportunity detection",
      provider: {
        "@type": "Organization",
        name: "Leadify AI",
        url: SITE_URL,
      },
      areaServed: {
        "@type": "Country",
        name: "Israel",
      },
      url: SITE_URL,
      description: isHebrew
        ? "שירות מנוהל מבוסס סוכני AI שסורק קבוצות פייסבוק בזמן אמת, מזהה לקוחות פוטנציאליים והזדמנויות עסקיות רלוונטיות לכל בעל מקצוע, ושולח אותן עם ניתוח והודעת פנייה מוכנה - למייל ולנייד."
        : "A managed, AI-agent service that scans Facebook groups in real time, identifies potential customers and relevant business opportunities for each professional, and delivers them with analysis and a ready-to-send outreach message - by email and push.",
    },
    // FAQPage — built from the same data the visible FAQ renders (faqData.js),
    // so the structured data always matches the on-screen text.
    buildFaqJsonLd(isHebrew),
  ];
};

// Exported for the SEO-invariant test, which renders the page body without the
// app chrome (AuthLayout drags in Navbar, Footer, the auth + guide contexts).
export const HomepageContent = ({ isHebrew = true }) => {
  const meta = isHebrew ? HOMEPAGE_META.he : HOMEPAGE_META.en;
  // Body background, the dev-only capture hooks and the fragment scroll. They
  // lived here as three private hooks until backlog #278: ProfessionLanding.js
  // carried a verbatim copy of all three, so any divergence could only be a
  // bug. Same effects, same order, no DOM change.
  useLiveMeshPageChrome();
  // A <Suspense> boundary cannot be hydrated from the prerendered snapshot:
  // client-rendered HTML carries no boundary markers, and React 19 answers a
  // markerless boundary by throwing the WHOLE root away and re-rendering it
  // (locked by __tests__/homepageHydration.test.js). So the mesh boundary is
  // mounted one render AFTER hydration - the canvas is lazy and decorative,
  // and the snapshot had nothing in that slot anyway. On a client-only boot
  // `hydrated` is true from the first render and nothing changes.
  const hydrated = useHydrated();
  // The ad-visitor hero: a Hebrew visit from the Google "buy leads" ad group
  // reads different hero words (adHeroCopy.js). The code is read ONCE, on the
  // first render - CaptureAcquisition strips it from the address bar right
  // after, so a later re-read could lose it when sessionStorage is blocked.
  // It is SHOWN only on a hydrated, non-prerender render: the snapshot and the
  // hydration pass both carry the default hero (no mismatch), and the swap
  // lands on the synchronous post-hydration render, before paint - no effect,
  // no flash frame. English has no approved ad copy and keeps the default.
  const [adSourceCode] = useState(getAdSourceCode);
  const adHero =
    isHebrew && hydrated && !isPrerendering() && adSourceCode === AD_HERO_SOURCE_CODE;
  // The ad-hero hold (public/index.html hides the hero copy for an r8 visit so
  // the snapshot's default words never paint). The variant is FINAL once
  // `hydrated` is true - ad copy or default alike - so the hold ends on that
  // commit: a layout effect, after the new words are in the DOM and before the
  // browser paints them. The hydration pass (hydrated false) still carries the
  // snapshot's default words and keeps the hold.
  useLayoutEffect(() => {
    if (hydrated) releaseAdHeroHold();
  }, [hydrated]);

  return (
    // `hl-root` scopes every design token in homepage.css and is what the
    // motion-off rules hang off. It must wrap the whole page, not one section.
    //
    // NO `overflow` here, ever. `overflow-x: hidden` on a non-root element makes
    // `overflow-y` compute to `auto`, which turns this Box into a scroll
    // container - and `position: sticky` in the pinned how-it-works section
    // then sticks to THIS box's never-scrolling scrollport instead of the
    // viewport, so the story scrolled off and the visitor saw a blank pin for
    // ~2.4 viewport heights (found by the post-cutover review; every build
    // capture used the debug hook that forces the stage without scrolling).
    // Horizontal clipping is already `body`'s job (index.css `overflow-x:
    // hidden`, which propagates to the viewport and creates no scrollport).
    <Box
      className="hl-root"
      sx={{
        width: "100%",
        background: "var(--hl-page)",
      }}
    >
      <Seo
        title={meta.title}
        description={meta.description}
        path="/"
        lang={isHebrew ? "he" : "en"}
        jsonLd={buildHomepageJsonLd(isHebrew)}
      />

      {/* The fixed background stack, painted back to front. DOM order is what
          puts the mesh above the washes - both sit at z-index 0. */}
      <SceneWashes />
      {hydrated && (
        <Suspense fallback={null}>
          <MeshCanvas />
        </Suspense>
      )}
      {/* The smoke plume's portal target (HowItWorksLive.js), sitting exactly
          where the prototype's #smoke canvas sits: after the mesh, before the
          grain, INSIDE the same stacking context as the sections. A fixed
          canvas portalled to <body> instead escapes AuthLayout's <main>
          (position: relative; z-index: 1) and paints over the whole page,
          panels included - that was the how-it-works plume bug. The slot
          renders no box of its own; it only sets the canvas's stacking order. */}
      <div id={SMOKE_SLOT_ID} className="hl-smoke-slot" aria-hidden="true" />
      <Box className="hl-grain" aria-hidden="true" />
      <Box className="hl-vignette" aria-hidden="true" />

      {/* Section order is the prototype's. Every section carries its own anchor
          id on its SectionShell (Navbar's scroll-spy observes those ids). */}
      <HeroLive isHebrew={isHebrew} {...(adHero ? AD_HERO_COPY : null)} />
      <TickerLive />
      <ClientsLive isHebrew={isHebrew} />
      <HowItWorksLive isHebrew={isHebrew} />
      <FeaturesLive isHebrew={isHebrew} />
      <TypesLive isHebrew={isHebrew} />
      <PricingSection isHebrew={isHebrew} />
      <NumbersLive isHebrew={isHebrew} />
      {/* Both CTA panels are named for the engagement tracker, as on a
          profession page: the mid-page panel and the closing band. */}
      <CtaLive isHebrew={isHebrew} trackSection="cta-mid" />
      <FaqLive isHebrew={isHebrew} />
      <BlogLive isHebrew={isHebrew} />
      {/* Second, compact ask at the very end - a page that ends on the blog ends on
          an exit, and a visitor who read this far is warm (owner-approved 2026-09-04). */}
      <CtaBandLive isHebrew={isHebrew} trackSection="cta-end" />
    </Box>
  );
};

// Pure marketing page: it renders, and nothing else. The "is this visitor
// signed in, and should they be here at all?" decision lives in
// components/RootRoute.js (backlog #158) — deciding it here meant the page had
// to mount, and paint, before it could redirect.
const Homepage = () => {
  return (
    <AuthLayout isPublic={true} navVariant="home">
      <HomepageContent />
    </AuthLayout>
  );
};

export default Homepage;
